import type {
  ContextKind,
  PageDescriptor,
  ProjectDescriptor,
  Table,
} from "@kaleidoscope-solutions/engine-wasm"
import type { Grid } from "@/logic/GridBuilder"
import type { WASM_METHODS } from "@/types"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import { defineStore } from "pinia"

import messageHandler, { UIMessageKind } from "@/logic/message-handler"
import { ALPHA_PROJECT_DETAIL_PAGES } from "@/utils/alphaModel"

export interface ViewTable
  extends Omit<Table, WASM_METHODS | "cellProperties"> {
  grid: Grid
  isListTable: boolean
}

interface PageOverlayState {
  projectName: string
  page: PageDescriptor
  context: ContextKind[]
}

interface OpenFormulaCreatePanelOptions {
  sourceViewName?: string
  sourceProjectName?: string
  sourcePageName?: string
  preloadFormulaName?: string
  preloadFormulaText?: string
  preloadFormulaType?: "Block" | "List"
  addSavedFormulaToSourceColumn?: boolean
  addSavedFormulaToNewViewOnSourcePage?: boolean
}

export type FormulaPanelOrientation = "vertical" | "horizontal"

const FORMULA_PANEL_STATE_STORAGE_KEY = "ks:formula-panel-state"
const FORMULA_PANEL_MIN_WIDTH = 420
const FORMULA_PANEL_MIN_HEIGHT = 260
const FORMULA_PANEL_EDGE_GUTTER = 18

interface PersistedFormulaPanelState {
  width?: number
  height?: number
  x?: number | null
  y?: number | null
  orientation?: FormulaPanelOrientation
}

function hasStorage() {
  try {
    return typeof globalThis.localStorage !== "undefined"
  } catch {
    return false
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value)
}

function parsePanelPosition(value: unknown): number | null {
  if (value == null) {
    return null
  }

  if (!isFiniteNumber(value)) {
    return null
  }

  return Math.max(0, Math.round(value))
}

function parsePanelSize(value: unknown, fallback: number, min: number): number {
  if (!isFiniteNumber(value)) {
    return fallback
  }

  return Math.max(min, Math.round(value))
}

function parseOrientation(value: unknown): FormulaPanelOrientation {
  return value === "horizontal" ? "horizontal" : "vertical"
}

function loadPersistedFormulaPanelState(): PersistedFormulaPanelState {
  if (!hasStorage()) {
    return {}
  }

  try {
    const raw = globalThis.localStorage.getItem(FORMULA_PANEL_STATE_STORAGE_KEY)
    if (!raw) {
      return {}
    }

    const parsed = JSON.parse(raw) as PersistedFormulaPanelState
    return {
      width: parsePanelSize(parsed.width, 760, FORMULA_PANEL_MIN_WIDTH),
      height: parsePanelSize(parsed.height, 420, FORMULA_PANEL_MIN_HEIGHT),
      x: parsePanelPosition(parsed.x),
      y: parsePanelPosition(parsed.y),
      orientation: parseOrientation(parsed.orientation),
    }
  } catch {
    return {}
  }
}

function persistFormulaPanelState(state: {
  formulaPanelWidth: number
  formulaPanelHeight: number
  formulaPanelX: number | null
  formulaPanelY: number | null
  formulaPanelOrientation: FormulaPanelOrientation
}) {
  if (!hasStorage()) {
    return
  }

  const payload: PersistedFormulaPanelState = {
    width: state.formulaPanelWidth,
    height: state.formulaPanelHeight,
    x: state.formulaPanelX,
    y: state.formulaPanelY,
    orientation: state.formulaPanelOrientation,
  }

  try {
    globalThis.localStorage.setItem(FORMULA_PANEL_STATE_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore storage write failures (private mode/full quota).
  }
}

function findPageByName(pages: PageDescriptor[] | undefined, targetName: string): PageDescriptor | undefined {
  if (!pages) {
    return undefined
  }

  const normalizedTarget = targetName.toLowerCase()
  for (const page of pages) {
    if (String(page.name ?? "").toLowerCase() === normalizedTarget) {
      return page
    }

    const nestedMatch = findPageByName(page.subPages, targetName)
    if (nestedMatch) {
      return nestedMatch
    }
  }

  return undefined
}

interface State {
  model?: ModelFacade
  project?: ProjectDescriptor
  pageDescriptor?: PageDescriptor
  overlays: PageOverlayState[]
  formulaCreatePanelOpen: boolean
  formulaCreatePanelSourceViewName: string | null
  formulaCreatePanelSourceProjectName: string | null
  formulaCreatePanelSourcePageName: string | null
  formulaCreatePanelPreloadName: string | null
  formulaCreatePanelPreloadText: string | null
  formulaCreatePanelPreloadType: "Block" | "List" | null
  formulaCreatePanelAddSavedFormulaToSourceColumn: boolean
  formulaCreatePanelAddSavedFormulaToNewViewOnSourcePage: boolean
  formulaPanelWidth: number
  formulaPanelHeight: number
  formulaPanelX: number | null
  formulaPanelY: number | null
  formulaPanelOrientation: FormulaPanelOrientation
  formulaSelectTableMode: boolean
  formulaHoveredTableToken: string | null
  formulaPendingInsertToken: string | null
}

export const usePageStore = defineStore("page", {
  state: (): State => {
    const persistedState = loadPersistedFormulaPanelState()
    return {
      model: undefined,
      project: undefined,
      pageDescriptor: undefined,
      overlays: [],
      formulaCreatePanelOpen: false,
      formulaCreatePanelSourceViewName: null,
      formulaCreatePanelSourceProjectName: null,
      formulaCreatePanelSourcePageName: null,
      formulaCreatePanelPreloadName: null,
      formulaCreatePanelPreloadText: null,
      formulaCreatePanelPreloadType: null,
      formulaCreatePanelAddSavedFormulaToSourceColumn: false,
      formulaCreatePanelAddSavedFormulaToNewViewOnSourcePage: false,
      formulaPanelWidth: persistedState.width ?? 760,
      formulaPanelHeight: persistedState.height ?? 420,
      formulaPanelX: persistedState.x ?? null,
      formulaPanelY: persistedState.y ?? null,
      formulaPanelOrientation: persistedState.orientation ?? "vertical",
      formulaSelectTableMode: true,
      formulaHoveredTableToken: null,
      formulaPendingInsertToken: null,
    }
  },
  actions: {
    initialise(model: ModelFacade) {
      this.model = model
    },
    setProject(project: ProjectDescriptor) {
      this.project = project
    },
    async setPage(pageDescriptor: PageDescriptor) {
      // Detail page in the engine injects extra widgets.
      if (this.project?.name === ALPHA_PROJECT_DETAIL_PAGES) {
        this.pageDescriptor = await this.modelApi.detailPage(pageDescriptor.name)
        return
      }

      this.pageDescriptor = pageDescriptor
    },
    async reloadProjectPage() {
      if (this.project === undefined || this.page === undefined) {
        return
      }
      const currentPageName = this.page?.name
      if (!currentPageName) {
        return
      }

      const project = await this.modelApi.project(this.project?.name)
      const page = findPageByName(project.pages, currentPageName)

      if (page === undefined) {
        throw new Error("Page not found")
      }

      await this.setPage(page)
    },
    clearOverlays() {
      this.overlays = []
    },
    removeLastOverlay() {
      this.overlays.pop()

      messageHandler.emit(UIMessageKind.OverlayClosed, undefined)
    },
    openFormulaCreatePanel(options?: OpenFormulaCreatePanelOptions) {
      this.formulaCreatePanelOpen = true
      this.formulaCreatePanelSourceViewName = options?.sourceViewName ?? null
      this.formulaCreatePanelSourceProjectName = options?.sourceProjectName ?? null
      this.formulaCreatePanelSourcePageName = options?.sourcePageName ?? null
      this.formulaCreatePanelPreloadName = options?.preloadFormulaName ?? null
      this.formulaCreatePanelPreloadText = options?.preloadFormulaText ?? null
      this.formulaCreatePanelPreloadType = options?.preloadFormulaType ?? null
      this.formulaCreatePanelAddSavedFormulaToSourceColumn = options?.addSavedFormulaToSourceColumn ?? false
      this.formulaCreatePanelAddSavedFormulaToNewViewOnSourcePage = options?.addSavedFormulaToNewViewOnSourcePage ?? false
      this.formulaHoveredTableToken = null
      this.formulaPendingInsertToken = null
    },
    closeFormulaCreatePanel() {
      this.formulaCreatePanelOpen = false
      this.formulaCreatePanelSourceViewName = null
      this.formulaCreatePanelSourceProjectName = null
      this.formulaCreatePanelSourcePageName = null
      this.formulaCreatePanelPreloadName = null
      this.formulaCreatePanelPreloadText = null
      this.formulaCreatePanelPreloadType = null
      this.formulaCreatePanelAddSavedFormulaToSourceColumn = false
      this.formulaCreatePanelAddSavedFormulaToNewViewOnSourcePage = false
      this.formulaHoveredTableToken = null
      this.formulaPendingInsertToken = null
    },
    setFormulaPanelWidth(width: number) {
      this.formulaPanelWidth = width
      persistFormulaPanelState(this)
    },
    setFormulaPanelHeight(height: number) {
      this.formulaPanelHeight = height
      persistFormulaPanelState(this)
    },
    setFormulaPanelPosition(position: { x: number; y: number }) {
      this.formulaPanelX = position.x
      this.formulaPanelY = position.y
      persistFormulaPanelState(this)
    },
    clampFormulaPanelPosition(viewport: { width: number; height: number }, panel: { width: number; height: number }) {
      if (this.formulaPanelX == null || this.formulaPanelY == null) {
        return
      }
      const maxX = Math.max(0, viewport.width - panel.width - FORMULA_PANEL_EDGE_GUTTER)
      const maxY = Math.max(0, viewport.height - panel.height - 8)
      this.formulaPanelX = Math.min(Math.max(8, this.formulaPanelX), maxX)
      this.formulaPanelY = Math.min(Math.max(8, this.formulaPanelY), maxY)
      persistFormulaPanelState(this)
    },
    setFormulaPanelOrientation(orientation: FormulaPanelOrientation) {
      this.formulaPanelOrientation = orientation
      persistFormulaPanelState(this)
    },
    toggleFormulaPanelOrientation() {
      this.formulaPanelOrientation = this.formulaPanelOrientation === "vertical"
        ? "horizontal"
        : "vertical"
      persistFormulaPanelState(this)
    },
    setFormulaSelectTableMode(value: boolean) {
      this.formulaSelectTableMode = value
    },
    setFormulaHoveredTableToken(token: string | null) {
      this.formulaHoveredTableToken = token
    },
    insertFormulaToken(token: string) {
      this.formulaPendingInsertToken = token
    },
  },
  getters: {
    modelApi(state) {
      if (state.model === undefined) {
        throw new Error("Model not initialised")
      }
      return state.model?.api
    },
    pages(state) {
      if (state.project === undefined) {
        return []
      }

      const pages: PageDescriptor[] = [state.project?.pages, ...state.project?.pages.map((page) => page.subPages)].flat()

      return pages
    },
    page(state): PageDescriptor | undefined {
      return state.pageDescriptor
    },
    hasOverlays(state) {
      return state.overlays.length > 0
    }
  },
})
